import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faCheck,
  faUser,
  faBookOpen,
  faMoneyBill,
  faCalendarAlt,
  faClock,
  faXmark
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { toastHelper } from "../../Helpers/toastHelper";
import ApiServices from "../Services/ApiServices";
import Loader from "../Shared/Loader";
import DeleteAlert from "../Shared/DeleteAlert";
import extractErrorMessage from "../../Helpers/APIHelper";
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import DynamicModal from '../Shared/DynamicModal';

const AdminCourseSubscription = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  const paymentStatusOptions = [
    { label: 'تم الدفع', value: '1' },
    { label: 'لم يتم الدفع', value: '2' },
  ];

  const subscriptionFormConfig = useMemo(() => {
    return [
      {
        name: 'course_id',
        label: 'الدورة',
        type: 'select',
        required: true,
        defaultValue: editingSubscription?.course_id ? String(editingSubscription.course_id) : '',
        options: courses.map((course) => ({ label: course.name, value: String(course.id) })),
      },
      {
        name: 'student_id',
        label: 'الطالب',
        type: 'select',
        required: true,
        defaultValue: editingSubscription?.student_id ? String(editingSubscription.student_id) : '',
        options: students.map((student) => ({ label: student.name, value: String(student.id) })),
      },
      {
        name: 'note',
        label: 'ملاحظات',
        type: 'textarea',
        required: false,
        defaultValue: editingSubscription?.note || '',
      },
      {
        name: 'payment_status',
        label: 'حالة الدفع',
        type: 'select',
        required: true,
        defaultValue: editingSubscription?.payment_status
          ? String(editingSubscription.payment_status)
          : '1',
        options: paymentStatusOptions,
      },
    ];
  }, [courses, students, editingSubscription]);

  // Fetch subscriptions
  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await ApiServices.get(
        `CourseSubscription/getAll?PageSize=${pageSize}&PageNumber=${currentPage}&IsActive=true&Word=${encodeURIComponent(searchQuery)}`
      );
      setSubscriptions(response.data.items);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      toastHelper.error("حدث خطأ أثناء تحميل الاشتراكات");
    } finally {
      setLoading(false);
    }
  };

  // Fetch courses and students for dropdowns
  const fetchCoursesAndStudents = async () => {
    try {
      const [coursesRes, studentsRes] = await Promise.all([
        ApiServices.get("Lookup/Platform/Courses"),
        ApiServices.get("Lookup/Platform/Students")
      ]);
      setStudents(studentsRes);
      setCourses(coursesRes);

    } catch (err) {
      toastHelper.error("حدث خطأ أثناء تحميل البيانات");
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    fetchCoursesAndStudents();
  }, [currentPage, searchQuery, pageSize]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle edit
  const handleEdit = (subscription) => {
    setEditingSubscription(subscription);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = (subscription) => {
    setSelectedSubscription(subscription);
    setShowDeleteAlert(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!selectedSubscription) return;

    setDeleteLoading(true);
    try {
      await ApiServices.delete(`CourseSubscription/delete/${selectedSubscription.id}`);
      toastHelper.success("تم حذف الاشتراك بنجاح");
      fetchSubscriptions();
      setShowDeleteAlert(false);
      setSelectedSubscription(null);
    } catch (error) {
      toastHelper.error("حدث خطأ أثناء حذف الاشتراك");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubscriptionModalSubmit = async (values) => {
    try {
      setFormLoading(true);

      const payload = {
        course_id: Number(values.course_id),
        student_id: Number(values.student_id),
        note: values.note || '',
        payment_status: Number(values.payment_status),
      };

      if (editingSubscription?.id) {
        await ApiServices.put(`CourseSubscription/update/${editingSubscription.id}`, payload);
      } else {
        await ApiServices.post('CourseSubscription/create', payload);
      }

      setShowForm(false);
      setEditingSubscription(null);
      toastHelper.success(editingSubscription?.id ? 'تم تحديث الاشتراك بنجاح' : 'تم إضافة الاشتراك بنجاح');
      fetchSubscriptions();
    } catch (error) {
      const title = error?.title || error?.message || '';
      if (title.toLowerCase().includes('validation errors')) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(error?.msg || 'حدث خطأ أثناء حفظ الاشتراك');
      }

      throw error;
    } finally {
      setFormLoading(false);
    }
  };


  if (deleteLoading) {
    return <Loader text="جاري حذف الاشتراك..." />;
  }
  if (formLoading) {
    return <Loader text="جاري حفظ الاشتراك..." />;
  }
  if (loading) {
    return <Loader text="جاري تحميل الاشتراكات..." />;
  }

  return (
    <div className="subscription-manager">
      <div className="section-header">
        <div className="header-left">
          <h2>إدارة اشتراكات الدورات</h2>


        </div>
        <div className="header-right">
          <button className="add-subscription-btn" onClick={() => setShowForm(true)}>
            <FontAwesomeIcon icon={faPlus} />
            إضافة اشتراك جديد
          </button>
        </div>
      </div>

      <DynamicModal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingSubscription(null);
        }}
        title={editingSubscription ? "تعديل الاشتراك" : "إضافة اشتراك جديد"}
        submitButtonText={editingSubscription ? "تحديث الاشتراك" : "إضافة الاشتراك"}
        formConfig={subscriptionFormConfig}
        onSubmit={handleSubscriptionModalSubmit}
        onSubmitError={(error) => {
          const title = error?.title || error?.message || '';
          if (title.toLowerCase().includes('validation errors')) {
            const errMessage = extractErrorMessage(error);
            toastHelper.error(errMessage);
          } else {
            toastHelper.error(error?.msg || 'حدث خطأ أثناء حفظ الاشتراك');
          }
        }}
      />

      <DeleteAlert
        isOpen={showDeleteAlert}
        onClose={() => {
          setShowDeleteAlert(false);
          setSelectedSubscription(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="تأكيد حذف الاشتراك"
        message="هل أنت متأكد من حذف هذا الاشتراك لن يتمكن الطالب من الوصول إلى الدورة نهائيا ؟"
        itemName={selectedSubscription?.student_name}
        itemDescription={`الدورة: ${selectedSubscription?.course_name}`}
        loading={deleteLoading}
      />

      <SoftGrid
        columns={[
          {
            key: 'student_name',
            label: 'الطالب',
            render: (value) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FontAwesomeIcon icon={faUser} />
                <span>{value}</span>
              </div>
            ),
          },
          {
            key: 'course_name',
            label: 'الدورة',
            render: (value) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FontAwesomeIcon icon={faBookOpen} />
                <span>{value}</span>
              </div>
            ),
          },
          {
            key: 'payment_status',
            label: 'حالة الدفع',
            render: (value) => (
              <div className={`payment-status status-${value}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FontAwesomeIcon icon={faMoneyBill} />
                <span>
                  {value === 2 ? 'لم يتم الدفع' : value === 1 ? 'تم الدفع' : 'معلق'}
                </span>
              </div>
            ),
          },
          {
            key: 'subscription_date',
            label: 'تاريخ الاشتراك',
            render: (value) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span>{value}</span>
              </div>
            ),
          },
          {
            key: 'note',
            label: 'ملاحظات',
            render: (value) => <span>{value || '-'}</span>,
          },
        ]}
        data={subscriptions}
        actions={[
          {
            tooltip: 'تعديل',
            icon: <FontAwesomeIcon icon={faEdit} />,
            onClick: handleEdit,
            className: 'edit-btn',
          },
          {
            tooltip: 'حذف',
            icon: <FontAwesomeIcon icon={faTrash} />,
            onClick: handleDelete,
            className: 'delete-btn',
          },
        ]}
        isLoading={loading}
        emptyStateMessage="لا توجد اشتراكات لعرضها"
        searchPlaceholder="ابحث عن اشتراك..."
        isPaginated={true}
        columnResizable={true}
        pageNumber={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onPageSizeChange={(newSize) => {
          setPageSize(newPageSize => {
            if (newPageSize !== newSize) {
              setCurrentPage(1);
              return newSize;
            }
            return newPageSize;
          });
        }}
        onSearch={(term) => { setSearchQuery(term); setCurrentPage(1); }}
        isColumnDraggable={true}
        isColumnSelectable={true}
      />

      <style jsx>{`
        .subscription-manager {
          padding: 2rem;
          min-height: 100vh;
          background: #fff;
          position: relative;
          overflow: hidden;
          border-radius: 1rem;
        }

        .section-header {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          border-radius: 24px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 2rem;
        }

        .header-left h2 {
          font-size: 2rem;
          font-weight: 700;
          background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
        }
        .add-subscription-btn {
          background-color: #6366f1;
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 16px;
          font-size: 1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
            font-family: "Zain", sans-serif;
        }

        .add-subscription-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(79, 70, 229, 0.3);
        }

        .subscriptions-table-container {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          border-radius: 24px;
          padding: 2rem;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          overflow-x: auto;
        }

        .subscriptions-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          min-width: 1000px;
        }

        .subscriptions-table th {
          text-align: right;
          padding: 1.5rem;
          font-weight: 600;
          color: #2d3748;
          border-bottom: 2px solid rgba(226, 232, 240, 0.8);
        }

        .subscription-row {
          transition: all 0.2s ease;
        }

        .subscription-row:hover {
          background: rgba(248, 250, 252, 0.8);
          transform: translateY(-1px);
        }

        .student-cell,
        .course-cell,
        .payment-cell,
        .date-cell,
        .note-cell,
        .actions-cell {
          padding: 1.5rem;
        }

        .student-info,
        .course-info,
        .date-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .student-info svg,
        .course-info svg,
        .date-info svg {
          color: #4f46e5;
          font-size: 1.2rem;
        }

        .payment-status {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .payment-status svg {
          font-size: 1rem;
        }

        .status-0 {
          background: #fee2e2;
          color: #dc2626;
        }

        .status-1 {
          background: #dcfce7;
          color: #16a34a;
        }

        .status-2 {
          background: #fef3c7;
          color: #d97706;
        }

        .note-cell p {
          margin: 0;
          color: #4a5568;
          font-size: 0.95rem;
          line-height: 1.6;
        }

        .action-buttons {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }

        .action-btn {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 0.75rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          min-width: 40px;
          min-height: 40px;
        }

        .action-btn:hover {
          background: #f1f5f9;
        }

        .action-btn.edit-btn {
          color: #4f46e5;
        }

        .action-btn.edit-btn:hover {
          background: #eef2ff;
        }

        .action-btn.delete-btn {
          color: #dc2626;
        }

        .action-btn.delete-btn:hover {
          background: #fef2f2;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(8px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-radius: 24px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .form-header {
          padding: 1.5rem;
          border-bottom: 2px solid rgba(226, 232, 240, 0.8);
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #e4e4f1;
          border-top-left-radius: 24px;
          border-top-right-radius: 24px;
        }

        .form-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #2d3748;
        }

        .subscription-form {
          padding: 1.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.75rem;
          font-size: 1rem;
          font-weight: 600;
          color: #2d3748;
        }

        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 1rem 1.5rem;
          border: 2px solid rgba(226, 232, 240, 0.8);
          border-radius: 16px;
          font-size: 1rem;
          color: #2d3748;
          background: rgba(255, 255, 255, 0.9);
          transition: all 0.3s ease;
        }

        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.1);
          background: white;
        }

        .form-group textarea {
          min-height: 100px;
          resize: vertical;
        }

        .form-actions {
          padding: 1.5rem;
          border-top: 2px solid rgba(226, 232, 240, 0.8);
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          background: #e4e4f1;
          border-bottom-left-radius: 24px;
          border-bottom-right-radius: 24px;
        }

        .cancel-btn,
        .submit-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          transition: all 0.3s ease;
        }

        .cancel-btn {
          background: #f1f5f9;
          color: #2d3748;
          border: none;
        }

        .submit-btn {
          background: #4f46e5;
          color: white;
          border: none;
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 1024px) {
          .subscription-manager {
            padding: 1rem;
          }

          .header-left {
            flex-direction: column;
            gap: 1rem;
          }
        }

        @media (max-width: 768px) {
          .section-header {
            flex-direction: column;
            gap: 1rem;
            padding: 1.5rem;
          }

          .header-left h2 {
            font-size: 1.5rem;
          }

          .add-subscription-btn {
            width: 100%;
            justify-content: center;
          }

          .subscriptions-table-container {
            padding: 1rem;
            margin: 0 -1rem;
            border-radius: 0;
          }

          .modal-content {
            max-width: 95%;
            margin: 1rem;
          }

          .form-header,
          .alert-header {
            padding: 1rem;
          }

          .form-header h2,
          .alert-header h2 {
            font-size: 1.25rem;
          }

          .subscription-form,
          .alert-content {
            padding: 1rem;
          }

          .form-actions,
          .alert-actions {
            padding: 1rem;
            flex-direction: column;
          }

          .form-actions button,
          .alert-actions button {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .subscription-manager {
            padding: 0.75rem;
          }

          .section-header {
            padding: 1rem;
          }

          .header-left h2 {
            font-size: 1.25rem;
          }

          .modal-content {
            max-width: 100%;
            margin: 0.5rem;
            border-radius: 16px;
          }

          .form-header,
          .alert-header {
            padding: 0.75rem;
          }

          .form-header h2,
          .alert-header h2 {
            font-size: 1.1rem;
          }

          .subscription-form,
          .alert-content {
            padding: 0.75rem;
          }

          .form-actions,
          .alert-actions {
            padding: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminCourseSubscription; 